import { AlertTriangle, ArrowRight, ExternalLink, Loader2, Users, X } from "lucide-react";
import type { AmbiguityReport, IdentityCandidate } from "@/lib/intel/disambiguation-types";
import { cn } from "@/lib/utils";

const VERIFICATION_LABEL: Record<IdentityCandidate["verification"], string> = {
  supported: "Supported · 3+ independent sources",
  likely: "Likely · 2 independent sources",
  possible: "Possible · 1 source",
  unverified: "Unverified",
};

function confidenceTone(value: number) {
  if (value >= 75) return "text-success";
  if (value >= 50) return "text-amber";
  return "text-muted-foreground";
}

/**
 * Shown BEFORE an investigation runs when a query could plausibly refer to more
 * than one real person or organisation. Picking a candidate narrows the scan;
 * nothing here is presented as a confirmed identification.
 */
export function DisambiguationPanel({
  report,
  busy,
  onSelect,
  onSearchAll,
  onDismiss,
}: {
  report: AmbiguityReport;
  busy: boolean;
  onSelect: (candidate: IdentityCandidate) => void;
  onSearchAll: () => void;
  onDismiss: () => void;
}) {
  return (
    <section className="col-span-12 rounded-xl border border-amber/40 bg-amber/5 p-3 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber">
            <AlertTriangle className="h-4 w-4" /> Ambiguous identity · {report.ambiguityScore}/100
          </div>
          <h2 className="mt-1 text-sm font-medium">
            “{report.target}” may refer to {report.candidates.length} different {report.targetType === "organization" ? "organisations" : "people"}.
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
            Pick the subject you mean so the investigation does not blend identities. Every candidate below is an
            evidence-backed lead from public sources, not a confirmed identification.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded p-1.5 text-muted-foreground hover:bg-panel hover:text-foreground"
          aria-label="Dismiss disambiguation"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {report.clarifiers.length > 0 && (
        <div className="mt-3 rounded-md border border-panel-border bg-background/40 p-2.5">
          <div className="font-mono text-[10px] uppercase text-cyan">Fastest way to narrow this</div>
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {report.clarifiers.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        {report.candidates.map((candidate) => (
          <article key={candidate.id} className="min-w-0 rounded-lg border border-panel-border bg-background/40 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{candidate.label}</div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  {[candidate.role, candidate.organization, candidate.locality].filter(Boolean).join(" · ") || "No public role reported"}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className={cn("font-display text-2xl font-semibold tabular-nums", confidenceTone(candidate.confidence))}>
                  {candidate.confidence}%
                </div>
                <div className="font-mono text-[9px] uppercase text-muted-foreground">
                  {candidate.independentSources} indep. src
                </div>
              </div>
            </div>

            <div className="mt-2 font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
              {VERIFICATION_LABEL[candidate.verification]}
            </div>

            {candidate.distinguishers.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs">
                {candidate.distinguishers.slice(0, 3).map((d) => (
                  <li key={d} className="text-muted-foreground">• {d}</li>
                ))}
              </ul>
            )}

            {candidate.aliases.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {candidate.aliases.slice(0, 6).map((a) => (
                  <span key={a} className="rounded border border-panel-border bg-panel/60 px-1.5 py-0.5 text-[10px]">{a}</span>
                ))}
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {candidate.sources.slice(0, 3).map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex max-w-full items-center gap-1 truncate font-mono text-[10px] text-cyan hover:underline"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  <span className="truncate">{s.domain || s.title}</span>
                </a>
              ))}
            </div>

            <button
              type="button"
              disabled={busy}
              onClick={() => onSelect(candidate)}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
              Investigate this one
            </button>
          </article>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onSearchAll}
          className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-2 text-xs hover:bg-panel disabled:opacity-60"
        >
          <Users className="h-3.5 w-3.5" />
          Investigate all candidates (identity confidence stays capped)
        </button>
        {report.reasons.slice(0, 2).map((r) => (
          <span key={r} className="text-[11px] text-muted-foreground">{r}</span>
        ))}
      </div>
    </section>
  );
}
